import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import {
    timeSeriesXAxis,
    colors,
    lineGraphDotSize,
    lineGridStyle,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import {
    getXAxisLabel,
    getYAxisLabel,
    getFormattedValue,
    getChartMargin,
} from '../../../dashboardUtils';

function TimeSeriesComposedBarLineGraph({
    dataPoints,
    barDataLabels,
    dataFormatter,
    lineDataLabels,
    chartProperties,
    dateFormatter,
    legendFormatter,
    thresholds,
    xAxisLabel,
    yAxisLabelLeft,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        barProperties,
        colorPalette = colors,
        composedChartProperties,
        lineProperties,
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        showSinglePointTooltip,
        suffix = '',
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const yAxisTickFormatter = (val) =>
        getFormattedValue(val, suffix, dataFormatter, timeFormat);

    const chartMargin = getChartMargin(
        dataPoints,
        timeSeriesXAxis,
        suffix,
        thresholds,
        timeFormat,
        yAxisTickFormatter,
        yAxisLabelLeft
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <ComposedChart {...composedChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={timeSeriesXAxis}
                    tickFormatter={dateFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={yAxisTickFormatter}
                    width={chartMargin.left}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={dateFormatter}
                            axisKey={timeSeriesXAxis}
                            dataFormat={timeFormat}
                            dataFormatter={dataFormatter}
                            legendFormatter={legendFormatter}
                            showSinglePoint={showSinglePointTooltip}
                            suffix={suffix}
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {barDataLabels.map((barLabel, index) => (
                    <Bar
                        {...barProperties}
                        key={barLabel}
                        dataKey={barLabel}
                        fill={colorPalette[index % colorPalette.length]}
                        onMouseOver={() => setActiveIndex(index)}
                        onMouseOut={() => setActiveIndex(-1)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(-1)}
                    />
                ))}
                {lineDataLabels.map((lineLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={lineLabel}
                        dataKey={lineLabel}
                        stroke={
                            colorPalette[
                                (barDataLabels.length + index) %
                                    colorPalette.length
                            ]
                        }
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () =>
                                setActiveIndex(barDataLabels.length + index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () =>
                                setActiveIndex(barDataLabels.length + index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        y={threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
}

TimeSeriesComposedBarLineGraph.propTypes = {
    barDataLabels: PropTypes.instanceOf(Array),
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func,
    dataPoints: PropTypes.instanceOf(Array),
    dateFormatter: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    lineDataLabels: PropTypes.instanceOf(Array),
    thresholds: PropTypes.instanceOf(Array).isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
    yAxisLabelRight: PropTypes.string,
};

TimeSeriesComposedBarLineGraph.defaultProps = {
    dataPoints: [],
    dataFormatter: null,
    barDataLabels: [],
    lineDataLabels: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
    yAxisLabelRight: null,
};

export default React.memo(
    TimeSeriesComposedBarLineGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
